// /en/police-clearance-foreigner/$nationality — EN per-nationality page
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { FOREIGNER_NATIONALITIES, getForeignerNationality } from "@/data/police-foreigner-nationalities";
import { FOREIGNER_VISA_TYPES } from "@/data/foreigner-visa-types";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { ContactCTA, FaqAccordion } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { ShieldCheck, MessageCircle, Phone, BadgeCheck, AlertTriangle, ListChecks, Globe2, FileCheck2 } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/police-clearance-foreigner/$nationality/")({
  loader: ({ params }) => {
    if (!getForeignerNationality(params.nationality)) throw notFound();
    return { slug: params.nationality };
  },
  head: ({ params }) => {
    const n = getForeignerNationality(params.nationality);
    if (!n) return { meta: [{ title: ctaTitle("Not found", "/en/police-clearance-foreigner/$nationality", "en")}] };
    const url = `${SITE}/en/police-clearance-foreigner/${n.slug}`;
    const title = `${n.homeAuthorityEn} for ${n.nameEn} Nationals in Thailand — from THB ${n.priceFromTh.toLocaleString()} | NYC`;
    const desc = `${n.oneLineEn} · ${n.timelineDays} · Handled by Lawyers Council-registered attorneys. Talk to a specialist today.`;
    return {
      meta: [
        { title: ctaTitle(title, "/en/police-clearance-foreigner/$nationality", "en") }, { name: "description", content: ctaDesc(desc, "/en/police-clearance-foreigner/$nationality", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/police-clearance-foreigner/$nationality", "en")},
        { property: "og:description", content: ctaDesc(desc, "/en/police-clearance-foreigner/$nationality", "en")},
        { property: "og:url", content: url },
        { property: "og:locale", content: "en_US" },
        { property: "og:type", content: "article" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/police-clearance-foreigner/${n.slug}`, `/en/police-clearance-foreigner/${n.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema(),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: `${n.homeAuthorityEn} procurement for ${n.nameEn} nationals in Thailand`,
              serviceType: "Foreign Police Clearance Procurement",
              areaServed: [{ "@type": "Country", name: "Thailand" }, { "@type": "Country", name: n.countryEn }],
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", telephone: "+66832494999", url: SITE },
              offers: { "@type": "Offer", price: n.priceFromTh, priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/en` },
                { "@type": "ListItem", position: 2, name: "Police Clearance", item: `${SITE}/en/police-clearance` },
                { "@type": "ListItem", position: 3, name: "For Foreigners in Thailand", item: `${SITE}/en/police-clearance-foreigner` },
                { "@type": "ListItem", position: 4, name: `${n.nameEn} Nationals`, item: url },
              ],
            }),
          },
        ]),
      ],
    };
  },
  component: NationalityEnPage,
});

function NationalityEnPage() {
  const { slug } = Route.useLoaderData() as { slug: string };
  const n = getForeignerNationality(slug)!;
  const related = FOREIGNER_NATIONALITIES.filter((x) => x.slug !== slug).slice(0, 8);
  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_55%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/en" className="hover:text-gold">Home</Link><span className="px-1.5 text-white/30">/</span>
            <Link to="/en/police-clearance-foreigner" className="hover:text-gold">Foreigners in Thailand</Link><span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{n.nameEn}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <ShieldCheck className="h-3.5 w-3.5" /> {n.countryEn} · {n.homeAuthorityEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            {n.homeAuthorityEn} for {n.nameEn} Nationals in Thailand
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            End-to-end procurement via {n.homeAuthorityEn} + {n.hague ? "home-country Apostille" : "Embassy Legalization"} + certified Thai translation with MFA endorsement — from THB {n.priceFromTh.toLocaleString()} · {n.timelineDays}.
          </p>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md">
              <MessageCircle className="h-4 w-4" /> LINE @nycli
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md">
              <Phone className="h-4 w-4" /> +66 83-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable
          </span>
          {n.oneLineEn}
        </blockquote>

        <section className="mt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">Common Use Cases</h2>
          <div className="mt-5 grid gap-2 sm:grid-cols-2">
            {n.commonUseCases.map((u, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800">✓ {u}</div>
            ))}
          </div>
        </section>

        <section className="mt-12">
          <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <ListChecks className="h-6 w-6 text-amber-600" /> End-to-End Process
          </h2>
          <ol className="mt-5 space-y-3 text-[15px]">
            <li className="rounded-2xl border border-slate-200 bg-white p-4"><strong className="text-navy-deep">1. Document intake:</strong> Passport + Thailand address + Power of Attorney (if required)</li>
            <li className="rounded-2xl border border-slate-200 bg-white p-4"><strong className="text-navy-deep">2. Home-authority filing:</strong> NYC submits via official {n.homeAuthorityEn} channel</li>
            <li className="rounded-2xl border border-slate-200 bg-white p-4"><strong className="text-navy-deep">3. Return to Thailand:</strong> Tracked international courier</li>
            <li className="rounded-2xl border border-slate-200 bg-white p-4"><strong className="text-navy-deep">4. {n.hague ? "Apostille" : "Legalization"}:</strong> {n.hague ? "Home-country competent authority stamp" : `Notary → ${n.countryEn} Foreign Ministry → ${n.embassyBkkEn}`}</li>
            <li className="rounded-2xl border border-slate-200 bg-white p-4"><strong className="text-navy-deep">5. Certified Thai translation + MFA:</strong> By MFA-registered translator + Thai Department of Consular Affairs endorsement</li>
            <li className="rounded-2xl border border-slate-200 bg-white p-4"><strong className="text-navy-deep">6. Delivery:</strong> NYC office pickup or courier to your address</li>
          </ol>
        </section>

        <section className="mt-12">
          <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <AlertTriangle className="h-6 w-6 text-rose-600" /> Common Pitfalls
          </h2>
          <ul className="mt-5 space-y-2">
            {n.pitfalls.map((p, i) => (
              <li key={i} className="rounded-2xl border border-rose-200 bg-rose-50/40 p-4 text-[14px] text-slate-800">⚠️ {p}</li>
            ))}
          </ul>
        </section>

        <section className="mt-12">
          <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <FileCheck2 className="h-6 w-6 text-amber-600" /> Which Thai Visa Are You Applying For?
          </h2>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            {FOREIGNER_VISA_TYPES.map((v) => (
              <Link key={v.slug} to="/police-clearance-foreigner/$nationality/for/$visa"
                params={{ nationality: slug, visa: v.slug }}
                className="group rounded-2xl border border-slate-200 bg-white p-4 hover:border-gold/50 hover:shadow-md transition">
                <div className="font-display text-sm font-extrabold text-navy-deep group-hover:text-amber-600">
                  {v.nameEn}
                </div>
                <div className="mt-1 text-[11px] text-slate-500">{v.codeTh} · {v.timelineEn}</div>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-12"><ContactCTA /></section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">FAQ</h2>
          <div className="mt-5">
            <FaqAccordion
              items={[
                { q: `Do I need to fly back to ${n.countryEn}?`, a: `Not usually — NYC coordinates via ${n.homeAuthorityEn} or ${n.embassyBkkEn} where permitted.` },
                { q: "How long does the full package take?", a: `${n.timelineDays} including translation and legalization.` },
                { q: "What is included in the price?", a: `From THB ${n.priceFromTh.toLocaleString()} — includes ${n.homeAuthorityEn} government fees, ${n.hague ? "Apostille" : "Embassy legalization"}, and certified Thai translation with MFA endorsement.` },
                { q: `Where is the ${n.countryEn} Embassy in Bangkok?`, a: n.embassyBkkEn },
                { q: "Are fingerprints required?", a: n.iso === "US" || n.iso === "CA" || n.iso === "SG" ? "Yes — NYC arranges fingerprinting at Thai police station with certified seal." : "Most nationals only need a name check — some cases require fingerprints (we advise during intake)." },
              ]}
            />
          </div>
        </section>

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="flex items-center gap-2 font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
            <Globe2 className="h-6 w-6 text-amber-600" /> Other Nationalities
          </h2>
          <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((r) => (
              <Link key={r.slug} to="/en/police-clearance-foreigner/$nationality" params={{ nationality: r.slug }}
                className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 hover:border-gold/50">
                <span className="font-semibold">{r.nameEn}</span>
                <span className="ml-auto text-[10px] text-slate-500">THB {r.priceFromTh.toLocaleString()}</span>
              </Link>
            ))}
          </div>
        </section>
      </div>

      <TrustLayer variant="compact" />
      <QuotableStatsStrip cluster="police" locale="en" variant="light" />
    </main>
  );
}
